/**
 * "All screens" — the Dock launcher's panel: the whole menu as a tree (any
 * depth, the shared MenuTree), plus Pinned (with reorder) and Recent (with
 * Clear), so everything the menu bar and the Dock abbreviate is one click
 * from the Dock.
 *
 * A non-modal dialog that opens AT its launcher — above it on a bottom Dock,
 * beside it on a side one — in front of every window. Esc or a click outside
 * closes it and focus returns to the launcher.
 */
import { X } from 'lucide-react'
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type RefObject } from 'react'
import { tw } from '../../../testHooks'
import { MenuTree } from '../../nav/parts/MenuTree'
import { PinnedList, RecentList } from '../../nav/parts/ShellLists'
import { S, iconButton } from '../../nav/tokens'
import type { ShellNavModel } from '../../nav/useShellNav'
import { glass } from './styles'
import type { DockSide } from './Settings'

export type AllScreensTab = 'menu' | 'pinned' | 'recent'

export function AllScreens({ model, tab, setTab, onClose, launcherRef, focusNodeId, emptyNavMessage, side }: {
  model: ShellNavModel
  tab: AllScreensTab
  setTab: (t: AllScreensTab) => void
  onClose: (refocus: boolean) => void
  launcherRef: RefObject<HTMLButtonElement | null>
  /** Focus this entry on open (a breadcrumb folder). */
  focusNodeId?: string | null
  emptyNavMessage: string
  /** Which edge the Dock (and so the launcher) is on. */
  side: DockSide
}) {
  const ref = useRef<HTMLDivElement>(null)
  // Anchored to the launcher, recomputed as the window resizes.
  const [pos, setPos] = useState<CSSProperties | null>(null)
  useLayoutEffect(() => {
    const place = () => {
      const b = launcherRef.current?.getBoundingClientRect()
      const vw = window.innerWidth
      const vh = window.innerHeight
      const M = 16
      const W = Math.min(720, vw - 2 * M)
      if (!b) { setPos({ left: (vw - W) / 2, top: M, width: W, height: Math.min(480, vh - 2 * M) }); return }
      if (side === 'bottom') {
        const H = Math.min(480, b.top - 14 - M)
        setPos({ left: Math.max(M, Math.min(b.left - 12, vw - W - M)), top: b.top - 14 - H, width: W, height: H })
      } else {
        const H = Math.min(480, vh - 2 * M)
        const top = Math.max(M, Math.min(b.top - 12, vh - H - M))
        const w = Math.min(W, side === 'left' ? vw - b.right - 14 - M : b.left - 14 - M)
        setPos(side === 'left'
          ? { left: b.right + 14, top, width: w, height: H }
          : { left: b.left - 14 - w, top, width: w, height: H })
      }
    }
    place()
    window.addEventListener('resize', place)
    return () => window.removeEventListener('resize', place)
  }, [side, launcherRef])

  useEffect(() => {
    const root = ref.current
    if (!root) return
    const target = (focusNodeId && root.querySelector<HTMLElement>(`[role="treeitem"][data-node-id="${CSS.escape(focusNodeId)}"]`))
      || root.querySelector<HTMLElement>('[role="treeitem"][tabindex="0"]')
      || root.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]')
    target?.focus()
  }, [focusNodeId])

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement
      if (ref.current?.contains(t)) return
      // The launcher toggles the panel itself; the entry menu sits above it.
      if (launcherRef.current?.contains(t) || t.closest?.('[role="menu"]')) return
      onClose(false)
    }
    window.addEventListener('mousedown', onDown)
    return () => window.removeEventListener('mousedown', onDown)
  }, [onClose, launcherRef])

  const tabs: { id: AllScreensTab; label: string; count?: number }[] = [
    { id: 'menu', label: 'All screens' },
    { id: 'pinned', label: 'Pinned', count: model.pinned.length },
    { id: 'recent', label: 'Recent', count: model.recent.length },
  ]
  const onTabKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const i = tabs.findIndex((t) => t.id === tab)
    const next = e.key === 'ArrowRight' ? i + 1 : e.key === 'ArrowLeft' ? i - 1
      : e.key === 'Home' ? 0 : e.key === 'End' ? tabs.length - 1 : null
    if (next === null) return
    e.preventDefault()
    const t = tabs[(next + tabs.length) % tabs.length]
    setTab(t.id)
    ;(e.currentTarget.querySelector(`[data-tab="${t.id}"]`) as HTMLElement | null)?.focus()
  }
  const picked = () => onClose(false)

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label="All screens"
      {...tw('action', 'shell-mac-all-screens-panel')}
      onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); onClose(true) } }}
      style={{
        // Fixed and high: in front of every desktop window.
        position: 'fixed', zIndex: 9441, ...(pos || { visibility: 'hidden' }),
        display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: 14,
        background: glass(S.panel, 94), color: S.text, border: `1px solid ${S.border}`,
        backdropFilter: 'blur(28px) saturate(180%)', WebkitBackdropFilter: 'blur(28px) saturate(180%)',
        boxShadow: '0 24px 60px rgba(0,0,0,0.3)',
      }}
    >
      <div style={{
        flexShrink: 0, display: 'flex', alignItems: 'center', gap: 10, minHeight: 52, padding: '0 8px 0 14px',
        borderBottom: `1px solid ${S.border}`,
      }}>
        <div role="tablist" aria-label="Show" onKeyDown={onTabKey} style={{
          display: 'flex', gap: 3, padding: 3, borderRadius: 9, background: S.surface2,
        }}>
          {tabs.map((t) => {
            const on = tab === t.id
            return (
              <button key={t.id} type="button" role="tab" data-tab={t.id} aria-selected={on}
                tabIndex={on ? 0 : -1} onClick={() => setTab(t.id)}
                {...tw('action', `shell-mac-tab:${t.id}`, { selected: on })}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5, height: 32, padding: '0 12px',
                  border: 'none', borderRadius: 7, cursor: 'pointer', fontFamily: 'inherit',
                  background: on ? S.panel : 'transparent', color: on ? S.text : S.text2,
                  fontSize: 14, fontWeight: on ? 600 : 500, boxShadow: on ? '0 1px 2px rgba(0,0,0,0.12)' : 'none',
                }}>
                {t.label}
                {typeof t.count === 'number' && t.count > 0 && (
                  <span style={{ fontSize: 13, color: S.text2, fontWeight: 500 }}>{t.count}</span>
                )}
              </button>
            )
          })}
        </div>
        <span style={{ flex: 1 }} />
        <button type="button" className="tw-sh-btn" onClick={() => onClose(true)} aria-label="Close all screens panel"
          title="Close" {...tw('action', 'shell-mac-all-screens-close')} style={iconButton(36)}>
          <X size={16} aria-hidden />
        </button>
      </div>
      <div role="tabpanel" aria-label={tabs.find((t) => t.id === tab)?.label}
        style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '6px 8px 12px' }}>
        {tab === 'menu' && (model.roots.length === 0
          ? <div style={{ padding: 16, fontSize: 14, color: S.text2, lineHeight: 1.5 }}>{emptyNavMessage}</div>
          : <MenuTree model={model} onPicked={picked} label="All screens" />)}
        {tab === 'pinned' && <PinnedList model={model} onPicked={picked} />}
        {tab === 'recent' && <RecentList model={model} onPicked={picked} />}
      </div>
    </div>
  )
}
